import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HardHat, TrendingUp, Building2, PencilRuler, FileSignature, ArrowRight, Menu,
  Home as HomeIcon, User, Building, DraftingCompass, Cog, Users, ClipboardList,
  FileText, ClipboardCheck, Search, CalendarClock, MapPinned, Target, Handshake,
  ShieldCheck, MessageSquare, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TopoBackground } from "@/components/TopoBackground";
import { useAdminAuth } from "@/context/AdminAuth";
import { MobileNav } from "@/components/MobileNav";
import { useSiteImages } from "@/lib/siteImages";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/83fd665c-12e1-46ec-b34b-7c468a7eac31/images/e7112c3fb1b86f7fc7ed5cb290f5734a0e6d3f95a3f95a2dac81c7e66b08b962.jpeg";

const iconCls = "h-6 w-6 text-[#163323]";
const IconTopo = () => (
  <svg viewBox="0 0 48 48" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 34 18 16l8 11 6-8 10 15" /><path d="M6 40h36" />
  </svg>
);
const IconCubes = () => (
  <svg viewBox="0 0 48 48" className={iconCls} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M24 6l10 6-10 6-10-6 10-6Z" /><path d="M14 20v12l10 6 10-6V20" />
  </svg>
);

const SERVICES = [
  { name: "Construction Surveying", Icon: HardHat },
  { name: "Topographical Surveys", Icon: IconTopo },
  { name: "Grading Levels / Slope Percentages", Icon: TrendingUp },
  { name: "Volume Calculations", Icon: IconCubes },
  { name: "Building Layouts", Icon: Building2 },
  { name: "Project As-Builts", Icon: ClipboardCheck },
  { name: "Plan Production", Icon: PencilRuler },
  { name: "Legal Descriptions", Icon: FileSignature },
  { name: "Surveying Reports", Icon: FileText },
];

const AUDIENCE = [
  { label: "Property Owners", Icon: HomeIcon },
  { label: "Contractors", Icon: HardHat },
  { label: "Developers", Icon: Building2 },
  { label: "Architects", Icon: DraftingCompass },
  { label: "Engineers", Icon: Cog },
  { label: "Construction Companies", Icon: Building },
  { label: "Project Managers", Icon: ClipboardList },
];

const STEPS = [
  { n: 1, title: "Tell Us About Your Project", Icon: FileText },
  { n: 2, title: "Project Review", Icon: Search },
  { n: 3, title: "Quotation & Scheduling", Icon: CalendarClock },
  { n: 4, title: "Surveying & Delivery", Icon: MapPinned },
];

const COMMITMENTS = [
  { label: "Accuracy", Icon: Target },
  { label: "Professionalism", Icon: Handshake },
  { label: "Reliability", Icon: ShieldCheck },
  { label: "Clarity", Icon: MessageSquare },
];

const GALLERY = [
  "https://images.unsplash.com/photo-1778041582108-2e765e738c8a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxsYW5kJTIwc3VydmV5b3IlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwdHJpcG9kfGVufDB8fHx8MTc4ODg4ODQ2Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1771068807150-2cab0734d25b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxsYW5kJTIwc3VydmV5b3IlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwdHJpcG9kfGVufDB8fHx8MTc4ODg4ODQ2Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1682663810771-89d21838530f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxsYW5kJTIwc3VydmV5b3IlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwdHJpcG9kfGVufDB8fHx8MTc4ODg4ODQ2Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1770822662903-aa69f6aa9b03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxsYW5kJTIwc3VydmV5b3IlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwdHJpcG9kfGVufDB8fHx8MTc4ODg4ODQ2Nnww&ixlib=rb-4.1.0&q=85",
];

const Brand = ({ light }) => (
  <Link to="/" className="flex items-center gap-3">
    <img src="/data-earth-logo.webp" alt="Data Earth Surveying" className="h-10 w-10 object-contain" />
    <span className="leading-none">
      <span className={`block font-serif text-lg font-bold tracking-[0.08em] ${light ? "text-white" : "text-[#163323]"}`}>DATA EARTH SURVEYING</span>
      <span className="mt-0.5 block text-[9px] font-medium uppercase tracking-[0.28em] text-[#5a6660]">Land · People · A Stronger Tomorrow</span>
    </span>
  </Link>
);

const SectionHead = ({ children, className = "" }) => (
  <h2 className={`font-serif text-3xl font-bold text-[#163323] sm:text-4xl ${className}`}>{children}</h2>
);

export default function About() {
  const navigate = useNavigate();
  const { img, overrides } = useSiteImages();
  const { openModal } = useAdminAuth();

  const nav = [
    ["Home", "/"], ["Our Services", "/services"], ["About Us", "/about"],
    ["Projects", "/projects"], ["Request a Survey", "/request"],
    ["Contact", "/contact"],
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-[#e6eae7] bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Brand />
          <nav className="hidden items-center gap-7 lg:flex">
            {nav.map(([label, href]) => {
              const active = label === "About Us";
              const cls = `relative text-sm font-medium transition-colors ${active ? "text-[#163323]" : "text-[#5a6660] hover:text-[#163323]"}`;
              return href.startsWith("/") ? (
                <Link key={label} to={href} className={cls}>
                  {label}{active && <span className="absolute -bottom-1.5 left-0 h-0.5 w-full rounded bg-[#c8a452]" />}
                </Link>
              ) : <a key={label} href={href} className={cls}>{label}</a>;
            })}
          </nav>
          <div className="hidden items-center gap-2 text-right xl:flex">
            <MapPin className="h-4 w-4 text-[#c8a452]" />
            <div className="text-[10px] font-semibold uppercase leading-tight tracking-[0.18em] text-[#5a6660]">
              Nassau, Bahamas<br /><span className="text-[#c8a452]">Surveying a Brighter Bahamas</span>
            </div>
          </div>
          <MobileNav buttonClassName="rounded-full bg-[#163323] p-2 text-white lg:hidden" />
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0d2116]">
        <img src={img("about_hero")} alt="Data Earth surveyor overlooking a Bahamian coastline" className="absolute inset-0 h-full w-full object-cover object-right" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d2116] via-[#163323]/85 to-transparent" />
        <div className="relative mx-auto max-w-[1400px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="max-w-2xl border-l-4 border-[#c8a452] pl-6">
            <h1 className="font-serif text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl">Accuracy You Can Build On</h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
              Data Earth Surveying provides professional land and construction surveying services for property owners,
              contractors, developers, architects, and project teams. We deliver reliable measurements, clear surveying
              information, and practical support throughout every stage of a project.
            </p>
          </motion.div>
        </div>
      </section>

      {/* MEET SHARIF + WHAT WE DO + WHO WE SERVE */}
      <section className="mx-auto max-w-[1400px] px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* Meet Sharif */}
          <div className="lg:col-span-4">
            <SectionHead>Meet Sharif</SectionHead>
            <div className="mt-6 overflow-hidden rounded-xl border border-[#e6eae7] bg-[#eef0ee]">
              {overrides?.about_sharif?.storage_path ? (
                <img src={img("about_sharif")} alt="Sharif — Data Earth Surveying" className="h-64 w-full object-cover" />
              ) : (
                <div className="flex h-64 items-end justify-center bg-gradient-to-b from-[#dfe4e0] to-[#cdd5cf]">
                  <User className="h-32 w-32 text-[#a7b2ab]" />
                </div>
              )}
            </div>
            <p className="mt-5 text-sm leading-relaxed text-[#4b544f]">
              Sharif is the surveying professional behind Data Earth Surveying. He works directly with clients to
              understand their properties, construction requirements, and project objectives before recommending the
              appropriate surveying service.
            </p>
          </div>

          {/* What We Do */}
          <div className="lg:col-span-5 lg:border-l lg:border-[#e6eae7] lg:pl-10">
            <SectionHead>What We Do</SectionHead>
            <div className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2">
              {SERVICES.map((s) => (
                <button key={s.name} onClick={() => navigate(`/request?service=${encodeURIComponent(s.name === "Grading Levels / Slope Percentages" ? "Grading & Slope Analysis" : s.name)}`)}
                  className="group flex items-start gap-3 text-left">
                  <s.Icon />
                  <span className="pt-0.5 text-sm font-medium text-[#1a221e] transition-colors group-hover:text-[#b38f3f]">{s.name}</span>
                </button>
              ))}
            </div>
            <Button data-testid="about-explore-services" onClick={() => navigate("/services")}
              className="mt-7 h-12 rounded-md bg-[#c1943a] px-6 font-semibold text-white hover:bg-[#a97f2c]">
              Explore Our Services <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Who We Serve */}
          <div className="lg:col-span-3 lg:border-l lg:border-[#e6eae7] lg:pl-10">
            <SectionHead>Who We Serve</SectionHead>
            <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-4 lg:grid-cols-2">
              {AUDIENCE.map((a) => (
                <div key={a.label} className="flex flex-col items-center text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#f6f1e3] text-[#8e6e23] ring-1 ring-[#e5d5ab]">
                    <a.Icon className="h-6 w-6" />
                  </span>
                  <span className="mt-2 text-xs font-medium leading-tight text-[#3d4742]">{a.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW WE WORK + COMMITMENTS */}
      <section className="relative overflow-hidden bg-[#f4f5f4] py-16 lg:py-20">
        <TopoBackground stroke="#163323" opacity={0.05} />
        <div className="relative mx-auto grid max-w-[1400px] gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <SectionHead>How We Work</SectionHead>
            <div className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-start">
              {STEPS.map((s, i) => (
                <div key={s.n} className="flex items-start gap-4 sm:flex-1 sm:flex-col sm:items-start">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#163323] font-mono text-sm font-bold text-white">{s.n}</span>
                    <s.Icon className="h-6 w-6 text-[#163323] sm:hidden" />
                    {i < STEPS.length - 1 && <ArrowRight className="hidden h-5 w-5 text-[#c8a452] sm:block" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <s.Icon className="hidden h-5 w-5 text-[#163323] sm:block" />
                    <span className="text-sm font-semibold text-[#1a221e]">{s.n}. {s.title}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 lg:border-l lg:border-[#dfe4e0] lg:pl-10">
            <SectionHead>Our Commitments</SectionHead>
            <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-4">
              {COMMITMENTS.map((c) => (
                <div key={c.label} className="flex flex-col items-center text-center">
                  <c.Icon className="h-8 w-8 text-[#163323]" strokeWidth={1.6} />
                  <span className="mt-3 text-sm font-semibold text-[#163323]">{c.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OUR WORK IN THE FIELD */}
      <section className="mx-auto max-w-[1400px] px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <SectionHead>Our Work in the Field</SectionHead>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {GALLERY.map((src, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="group relative overflow-hidden rounded-xl border border-[#e6eae7]">
              <img src={img(`about_field_${i + 1}`)} alt="Data Earth surveying project" className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#163323] to-[#0d2116] py-16 text-white">
        <TopoBackground opacity={0.07} />
        <div className="relative mx-auto flex max-w-[1400px] flex-col gap-6 px-4 text-center sm:px-6 lg:px-8">
          <div>
            <SectionHead className="text-white">Let's Discuss Your Surveying Needs</SectionHead>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-white/70">
              Tell us about your property or construction project, and we'll help you determine the appropriate surveying service.
            </p>
          </div>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <Button data-testid="about-request-btn" onClick={() => navigate("/request")}
              className="h-12 rounded-md bg-[#c1943a] px-7 font-semibold text-white hover:bg-[#a97f2c]">
              Request a Survey <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <a href="mailto:dataearthsurveying@gmail.com">
              <Button data-testid="about-contact-btn" variant="outline"
                className="h-12 w-full rounded-md border-white/40 bg-transparent px-7 font-semibold text-white hover:bg-white/10 hover:text-white sm:w-auto">
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 bg-[#0d2116] text-white">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center gap-4 px-4 py-6 sm:px-6 lg:flex-row lg:justify-between lg:px-8">
          <div className="flex items-center gap-4">
            <Brand light />
            <span className="hidden h-8 w-px bg-white/20 sm:block" />
            <span className="hidden font-serif text-sm italic text-white/80 sm:block">Accurate. Reliable. Built for Your Project.</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            <Link to="/" className="hover:text-[#c8a452]">Home</Link>
            <Link to="/services" className="hover:text-[#c8a452]">Our Services</Link>
            <Link to="/about" className="hover:text-[#c8a452]">About Us</Link>
            <Link to="/request" className="hover:text-[#c8a452]">Request a Survey</Link>
            <a href="mailto:dataearthsurveying@gmail.com" className="hover:text-[#c8a452]">Contact</a>
            <button onClick={openModal} data-testid="footer-admin-link" className="hover:text-[#c8a452]">Admin Portal</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
